export const Tabs = (() => {
	'use strict';

	// Constants
	const SELECTORS = {
		TABS_CONTAINER: '.s2block--tabs',
		TAB_NAV: '.tabs__nav',
		TAB_ITEM: '.s2block--tab-item',
		TAB_NAV_ITEM: '.tabs__nav-item a',
		TAB_CONTENT_CONTAINER: '.tab-content-container'
	};

	const CLASSES = {
		ACTIVE: 'active',
		VISIBLE: 'visible',
		TAB_NAV_ITEM: 'tabs__nav-item'
	};

	function init() {
		try {
			addTabs();
			handleTabClick();
			handleHashNavigation();
			handleSearchResultNavigation();
		} catch (error) {
			console.error('Tabs initialization failed:', error);
		}
	}

	function addTabs() {
		const tabBlocks = document.querySelectorAll(SELECTORS.TABS_CONTAINER);
		
		if (!tabBlocks.length) return;

		tabBlocks.forEach(tabBlock => {
			try {
				initializeTabBlock(tabBlock);
			} catch (error) {
				console.error('Failed to initialize tab block:', error);
			}
		});
	}

	function initializeTabBlock(tabBlock) {
		const tabNavigation = tabBlock.querySelector(SELECTORS.TAB_NAV);
		const tabItems = tabBlock.querySelectorAll(SELECTORS.TAB_ITEM);
		const hash = window.location.hash.substring(1);
		const decodedHash = hash ? decodeURIComponent(hash) : '';
		
		if (!tabNavigation || !tabItems.length) return;

		tabItems.forEach((tabItem, index) => {
			createTabNavigationItem(tabItem, tabNavigation, index, decodedHash);
		});
	}

	function createTabNavigationItem(tabItem, tabNavigation, index, hash) {
		const tabTitle = tabItem.dataset.content;
		const originalTitle = tabItem.dataset.originalTitle;
		if (!tabTitle) return;

		const tabId = generateTabId(tabTitle);
		const displayTitle = originalTitle || formatDisplayTitle(tabTitle);
		
		const tabNavigationItem = document.createElement('li');
		tabNavigationItem.classList.add(CLASSES.TAB_NAV_ITEM);
		tabNavigationItem.innerHTML = `<a href="#${tabId}" id="${tabId}" data-text="${displayTitle}">${displayTitle}</a>`;
		tabNavigation.appendChild(tabNavigationItem);
		
		// Set initial active state
		setInitialActiveState(tabItem, tabNavigationItem, tabTitle, hash, index);
	}

	function generateTabId(title) {
		// Handle special characters and create a safe ID
		return title
			.toLowerCase()
			.replace(/[^\w\s-]/g, '') // Remove special characters except spaces and hyphens
			.replace(/\s+/g, '-') // Replace spaces with hyphens
			.replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
			.replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
	}

	function formatDisplayTitle(title) {
		// Decode HTML entities first
		const decodedTitle = decodeHTMLEntities(title);
		return decodedTitle.replace(/-/g, ' ').replace(/\w\S*/g, (txt) => 
			txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
		);
	}

	function decodeHTMLEntities(text) {
		const textarea = document.createElement('textarea');
		textarea.innerHTML = text;
		return textarea.value;
	}

	function setInitialActiveState(tabItem, tabNavigationItem, tabTitle, hash, index) {
		// Decode the hash and compare with the generated tab ID
		const decodedHash = hash ? decodeURIComponent(hash) : '';
		const tabId = generateTabId(tabTitle);
		const shouldBeActive = (hash && tabId === decodedHash) || (!hash && index === 0);
		
		if (shouldBeActive) {
			activateTab(tabItem, tabNavigationItem);
		}
	}

	function activateTab(tabItem, tabNavigationItem) {
		// Activate navigation item
		const tabLink = tabNavigationItem.querySelector('a');
		if (tabLink) {
			tabLink.classList.add(CLASSES.ACTIVE);
		}
		
		// Activate tab content
		tabItem.classList.add(CLASSES.ACTIVE);
		
		// Activate content container
		const tabContentContainer = tabItem.querySelector(SELECTORS.TAB_CONTENT_CONTAINER);
		if (tabContentContainer) {
			tabContentContainer.classList.add(CLASSES.VISIBLE);
		}
	}

	function handleTabClick() {
		const tabLinks = document.querySelectorAll(SELECTORS.TAB_NAV_ITEM);
		
		tabLinks.forEach(tabLink => {
			tabLink.addEventListener('click', handleTabClickEvent);
		});
	}

	function handleTabClickEvent(e) {
		e.preventDefault();
		
		try {
			const target = e.target;
			const tabId = target.getAttribute('id');
			
			if (!tabId) return;
			
			switchToTab(tabId);
			updateUrlHash(tabId);
		} catch (error) {
			console.error('Tab click handling failed:', error);
		}
	}

	function switchToTab(tabId) {
		// Remove active state from all tabs
		clearAllActiveStates();
		
		// Find and activate the target tab
		// First try to find by the exact tabId
		let targetTabContent = document.querySelector(`[data-content="${tabId}"]`);
		let targetTabLink = document.querySelector(`#${tabId}`);
		
		// If not found, try to find by matching the generated ID
		if (!targetTabContent || !targetTabLink) {
			const tabItems = document.querySelectorAll(SELECTORS.TAB_ITEM);
			tabItems.forEach(tabItem => {
				const itemTitle = tabItem.dataset.content;
				if (itemTitle && generateTabId(itemTitle) === tabId) {
					targetTabContent = tabItem;
					targetTabLink = document.querySelector(`#${tabId}`);
				}
			});
		}
		
		if (targetTabContent && targetTabLink) {
			activateTabContent(targetTabContent, targetTabLink);
		}
	}

	function clearAllActiveStates() {
		// Clear navigation links
		const tabLinks = document.querySelectorAll(SELECTORS.TAB_NAV_ITEM);
		tabLinks.forEach(link => link.classList.remove(CLASSES.ACTIVE));
		
		// Clear tab contents
		const tabContents = document.querySelectorAll(SELECTORS.TAB_ITEM);
		tabContents.forEach(content => content.classList.remove(CLASSES.ACTIVE));
	}

	function activateTabContent(tabContent, tabLink) {
		// Activate tab link
		tabLink.classList.add(CLASSES.ACTIVE);
		
		// Activate tab content
		tabContent.classList.add(CLASSES.ACTIVE);
		
		// Animate content container
		animateContentContainer(tabContent);
	}

	function animateContentContainer(tabContent) {
		const tabContentContainer = tabContent.querySelector(SELECTORS.TAB_CONTENT_CONTAINER);
		
		if (tabContentContainer) {
			// Remove and re-add visible class to trigger animation
			tabContentContainer.classList.remove(CLASSES.VISIBLE);
			tabContentContainer.offsetHeight; // Force reflow
			tabContentContainer.classList.add(CLASSES.VISIBLE);
		}
	}

	function updateUrlHash(tabId) {
		try {
			history.pushState(null, null, `#${tabId}`);
		} catch (error) {
			console.error('Failed to update URL hash:', error);
		}
	}

	function handleHashNavigation() {
		// Handle browser back/forward buttons
		window.addEventListener('popstate', () => {
			const hash = window.location.hash.substring(1);
			if (hash) {
				const decodedHash = decodeURIComponent(hash);
				switchToTab(decodedHash);
			}
		});

		// Handle direct links with hash
		window.addEventListener('hashchange', () => {
			const hash = window.location.hash.substring(1);
			if (hash) {
				const decodedHash = decodeURIComponent(hash);
				switchToTab(decodedHash);
			}
		});
	}

	function handleSearchResultNavigation() {
		// Check for search_result parameter
		const urlParams = new URLSearchParams(window.location.search);
		const searchTerm = urlParams.get('search_result');
		
		if (searchTerm) {
			// This came from a search result, find and open relevant tab
			const relevantTab = findRelevantTab(searchTerm);
			if (relevantTab) {
				openTabByContent(relevantTab);
			}
		}
	}

	function findRelevantTab(searchTerm) {
		const tabItems = document.querySelectorAll(SELECTORS.TAB_ITEM);
		let bestMatch = null;
		let bestScore = 0;
		
		tabItems.forEach(tabItem => {
			const score = calculateRelevanceScore(tabItem, searchTerm);
			if (score > bestScore) {
				bestScore = score;
				bestMatch = tabItem;
			}
		});
		
		return bestMatch;
	}

	function calculateRelevanceScore(tabItem, searchTerm) {
		let score = 0;
		const searchTermLower = searchTerm.toLowerCase();
		
		// Get all text content from the tab
		const tabContent = tabItem.querySelector(SELECTORS.TAB_CONTENT_CONTAINER);
		if (!tabContent) return 0;
		
		const textContent = tabContent.textContent.toLowerCase();
		
		// Check for exact matches (highest score)
		if (textContent.includes(searchTermLower)) {
			score += 10;
		}
		
		// Check for word matches
		const searchWords = searchTermLower.split(' ');
		searchWords.forEach(word => {
			if (word.length > 2 && textContent.includes(word)) {
				score += 3;
			}
		});
		
		// Check tab title relevance
		const tabTitle = tabItem.dataset.content || '';
		const tabTitleLower = tabTitle.toLowerCase();
		if (tabTitleLower.includes(searchTermLower)) {
			score += 5;
		}
		
		// Check for partial matches
		if (searchTermLower.length > 3) {
			const partialMatches = textContent.split(' ').filter(word => 
				word.includes(searchTermLower) || searchTermLower.includes(word)
			);
			score += partialMatches.length;
		}
		
		return score;
	}

	function openTabByContent(tabItem) {
		const tabContent = tabItem.dataset.content;
		if (tabContent) {
			const tabId = generateTabId(tabContent);
			switchToTab(tabId);
		}
	}

	return {
		init: init,
	};
})();
